import classNames from "classnames";
import { extendTailwindMerge } from "tailwind-merge";

type AdditionalClassGroupIDs = "typography" | "dsTypography" | "dsRadius" | "buttonSize" | "buttonVariant";

/**
 * The design system's composite type utilities (`packages/ui/base/typography-utilities.css`)
 * are all named `text-*`, so tailwind-merge's built-in `text-color` group swallows them:
 * `cn("text-label-xl-500", "text-ref-earth-900")` used to collapse to just the color, silently
 * dropping the font/size/tracking. Registering them as their own group — a literal match, which
 * beats `text-color`'s validator — makes the two independent, as they are in CSS.
 *
 * Families and steps are enumerated rather than pattern-matched so an unknown `text-foo-bar`
 * still falls through to the built-in groups instead of being treated as typography.
 *
 * Kept in sync with the identical list in apps/web/src/libs/logic/utils/cn.ts.
 */
const DS_TYPOGRAPHY = [
  "label-xxl",
  "label-xl",
  "label-lg",
  "label-md",
  "label-sm",
  "label-xs",
  "label-mono-lg",
  "label-mono-md",
  "label-mono-sm",
  "footnote-md",
  "footnote-sm",
  "footnote-xs",
  "title-xxl",
  "title-xl",
  "title-lg",
  "title-md",
  "title-sm",
  "body-lg",
  "body-md",
  "body-sm",
  "display-md",
  "display-sm",
  "display-xs",
  "metric-lg",
  "metric-md",
  "metric-sm",
].flatMap((family) => ["400", "500", "600"].map((weight) => `${family}-${weight}`));

/**
 * The design system's named radii (`--radius-button`, `--radius-icon`, …) are custom theme keys
 * tailwind-merge can't recognise, so `cn("rounded-button", "rounded-icon")` kept both and left the
 * winner to stylesheet order. Enumerating them makes the override deterministic.
 */
const DS_RADIUS = ["button", "icon", "round", "minor", "bar-badge"];

const customTwMerge = extendTailwindMerge<AdditionalClassGroupIDs>({
  extend: {
    classGroups: {
      typography: ["h1", "h2", "h3"],
      dsTypography: [{ text: DS_TYPOGRAPHY }],
      dsRadius: [{ rounded: DS_RADIUS }],
      buttonSize: ["btn-normal", "btn-small", "btn-large"],
      buttonVariant: ["btn-primary", "btn-secondary", "btn-tertiary"],
    },
  },
});

export function cn(...args: classNames.ArgumentArray) {
  return customTwMerge(classNames(...args));
}
